import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase.ts';
import { agentChat, type ChatMessageRow, type ChatSession } from '@/lib/agentChat.ts';

/**
 * The chat panel's own state, kept apart from `RiggProvider`'s big reducer.
 *
 * Everything else in this app funnels through one project-wide store because
 * every screen reads the same tasks. The chat does not: it is one person
 * talking to one session, nobody else's business, so it gets its own small
 * subscription instead of adding two more tables' worth of cases to a reducer
 * built for something else. It follows the same rule anyway — reads come
 * straight out of Postgres, writes go through the edge function — just at a
 * smaller scale.
 *
 * A turn in progress is *not* read back from Postgres while it streams: the
 * NDJSON response from `agent-chat/sessions/:id/messages` is the live view,
 * appended locally as it arrives, because the incremental deltas are never
 * persisted (only the finished text is) and re-reading mid-stream would just
 * flicker. Once a turn ends, one reload reconciles local state with what
 * actually landed — the same "reconnect, then re-read" idea `RiggProvider`
 * uses for its own socket.
 */

export interface ChatLine {
  id: string;
  role: 'user' | 'assistant' | 'status' | 'error';
  body: string;
  /** Still being written — a status line being replaced, or text still streaming in. */
  pending?: boolean;
}

export function useAgentChat(projectId: string | null, userId: string | null) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  /* ── the session list ──────────────────────────────────────────────────── */

  useEffect(() => {
    if (!projectId || !userId) {
      setSessions([]);
      setActiveIdState(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from('agent_chat_sessions')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .is('archived_at', null)
        .order('updated_at', { ascending: false });
      if (cancelled) return;
      const rows = (data as ChatSession[] | null) ?? [];
      setSessions(rows);
      setActiveIdState((current) => (current && rows.some((r) => r.id === current) ? current : (rows[0]?.id ?? null)));
    };
    void load();

    const channel = supabase
      .channel(`rigg:chat-sessions:${projectId}:${userId}:${crypto.randomUUID().slice(0, 8)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'agent_chat_sessions', filter: `project_id=eq.${projectId}` },
        () => void load(),
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [projectId, userId]);

  /* ── the active session's transcript ──────────────────────────────────── */

  const reloadMessages = useCallback(async (sessionId: string) => {
    const { data } = await supabase
      .from('agent_chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    setMessages(
      ((data as ChatMessageRow[] | null) ?? []).map((row) => ({ id: row.id, role: row.role, body: row.body })),
    );
  }, []);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    void reloadMessages(activeId);

    const channel = supabase
      .channel(`rigg:chat-messages:${activeId}:${crypto.randomUUID().slice(0, 8)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'agent_chat_messages', filter: `session_id=eq.${activeId}` },
        () => {
          // A turn this tab is streaming already shows the live version;
          // reloading over it would replace a smooth stream with a jump cut.
          if (streamingRef.current || cancelled) return;
          void reloadMessages(activeId);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [activeId, reloadMessages]);

  /* ── sessions ──────────────────────────────────────────────────────────── */

  const newSession = useCallback(async () => {
    if (!projectId) return;
    const { session } = await agentChat.createSession(projectId);
    setSessions((current) => [session, ...current]);
    setActiveIdState(session.id);
  }, [projectId]);

  const deleteSession = useCallback(
    async (id: string) => {
      await agentChat.deleteSession(id);
      setSessions((current) => current.filter((s) => s.id !== id));
      setActiveIdState((current) => (current === id ? null : current));
    },
    [],
  );

  /* ── sending ───────────────────────────────────────────────────────────── */

  const send = useCallback(
    async (text: string) => {
      if (!projectId) return;
      let sessionId = activeId;
      if (!sessionId) {
        // In-thread like every other failure. This used to throw past the
        // caller into a toast, so the same underlying fault reported itself
        // two different ways depending on whether a session already existed.
        try {
          const { session } = await agentChat.createSession(projectId);
          setSessions((current) => [session, ...current]);
          setActiveIdState(session.id);
          sessionId = session.id;
        } catch (err) {
          const message = (err as Error).message;
          setError(message);
          setMessages((current) => [
            ...current,
            { id: `local-user-${crypto.randomUUID()}`, role: 'user', body: text },
            { id: `err-${crypto.randomUUID()}`, role: 'error', body: message },
          ]);
          return;
        }
      }
      const id = sessionId;

      streamingRef.current = true;
      setBusy(true);
      setError(null);
      const controller = new AbortController();
      abortRef.current = controller;

      setMessages((current) => [...current, { id: `local-user-${crypto.randomUUID()}`, role: 'user', body: text }]);

      let draftId: string | null = null;
      let draftText = '';
      let failure: string | null = null;

      try {
        for await (const frame of agentChat.sendMessage(id, text, controller.signal)) {
          switch (frame.type) {
            case 'status':
              setMessages((current) => [
                ...current.filter((m) => m.id !== 'live-status'),
                { id: 'live-status', role: 'status', body: frame.activity ?? '…', pending: true },
              ]);
              break;

            case 'text_delta': {
              draftId = draftId ?? `draft-${frame.id ?? crypto.randomUUID()}`;
              draftText += frame.text ?? '';
              const currentDraftId = draftId;
              setMessages((current) => [
                ...current.filter((m) => m.id !== currentDraftId),
                { id: currentDraftId, role: 'assistant', body: draftText, pending: true },
              ]);
              break;
            }

            case 'text_final': {
              const finalText = frame.text ?? draftText;
              const closingDraftId = draftId;
              setMessages((current) => [
                ...current.filter((m) => m.id !== closingDraftId && m.id !== 'live-status'),
                { id: `final-${frame.id ?? crypto.randomUUID()}`, role: 'assistant', body: finalText },
              ]);
              draftId = null;
              draftText = '';
              break;
            }

            case 'done':
              setMessages((current) => current.filter((m) => m.id !== 'live-status'));
              break;

            case 'error': {
              const message = frame.message ?? 'something went wrong';
              setError(message);
              setMessages((current) => [
                ...current.filter((m) => m.id !== 'live-status'),
                { id: `err-${crypto.randomUUID()}`, role: 'error', body: message },
              ]);
              break;
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') failure = (err as Error).message;
      } finally {
        streamingRef.current = false;
        setBusy(false);
        abortRef.current = null;

        // Reload first, then re-add the failure. A request that died before
        // the server started streaming persisted nothing but the user's own
        // message, so the reload is authoritative for everything *except*
        // why it went wrong — and reloading over the top of a locally-added
        // error line is how this used to swallow 502s whole: two messages
        // sent, nothing back, no error, no clue.
        await reloadMessages(id);
        if (failure) {
          const message = failure;
          setError(message);
          setMessages((current) => [
            ...current.filter((m) => m.id !== 'live-status'),
            { id: `err-${crypto.randomUUID()}`, role: 'error', body: message },
          ]);
        }
      }
    },
    [activeId, projectId, reloadMessages],
  );

  const interrupt = useCallback(() => {
    abortRef.current?.abort();
    if (activeId) void agentChat.interrupt(activeId).catch(() => undefined);
  }, [activeId]);

  return {
    sessions,
    activeId,
    setActiveId: setActiveIdState,
    messages,
    busy,
    error,
    send,
    newSession,
    deleteSession,
    interrupt,
  };
}
